import { ReactNode, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserMFA } from "@/hooks/profile/useUserMFA";
import { UserMFAVerify } from "@/components/auth/UserMFAVerify";
import { LoadingScreen } from "@/components/ui/loading-screen";

interface UserMFAGuardProps {
  children: ReactNode;
  enforceForRoles?: string[]; // roles that must pass MFA (default: sponsor)
}

interface MFAStatus {
  enabled: boolean;
  verified: boolean;
}

export function UserMFAGuard({
  children,
  enforceForRoles = ["sponsor", "artist", "fan"],
}: UserMFAGuardProps) {
  const { userRole, isLoading: isAuthLoading } = useAuth();
  const { checkMFAStatus } = useUserMFA();
  const [checkingMFA, setCheckingMFA] = useState(true);
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);

  const shouldEnforce = userRole ? enforceForRoles.includes(userRole) : false;

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!shouldEnforce) {
        setMfaStatus({ enabled: false, verified: true });
        setCheckingMFA(false);
        return;
      }
      try {
        const status = await checkMFAStatus();
        if (!mounted) return;
        setMfaStatus({ enabled: status.enabled, verified: status.verified });
      } finally {
        if (mounted) setCheckingMFA(false);
      }
    };
    if (!isAuthLoading) run();
    return () => {
      mounted = false;
    };
  }, [isAuthLoading, shouldEnforce, checkMFAStatus]);

  // Show loading screen until auth and MFA check complete
  if (isAuthLoading || checkingMFA || !mfaStatus) {
    return <LoadingScreen message="Checking security…" />;
  }

  // Show MFA verification if required and not verified
  if (shouldEnforce && mfaStatus.enabled && !mfaStatus.verified) {
    return (
      <UserMFAVerify
        onVerified={() => setMfaStatus({ ...mfaStatus, verified: true })}
      />
    );
  }

  return <>{children}</>;
}
